/* import { useTranslation } from 'react-i18next'; */
import React, { useEffect, useCallback } from "react";
import PublicWrapper from "app/components/PublicWrapper";
import { useSelector, useDispatch, shallowEqual } from "react-redux";
import { RootState } from "typedefs";
import { useTranslation } from "react-i18next";
import { Loading } from "app/components/Wrappers";
import SessionForm from "../../Sessions/SessionForm";
import CoachDetail from "../../Sessions/CoachDetail";
import useBookSessionData from "../../Sessions/hooks/useBookSessionData";
import { defaultSelection } from "../../Sessions/helpers";
import { setBookSession } from "app/actions/scheduling/bookSession";
import { schedulingFetchGet } from "app/utils/request/scheduling/fetch";
import { useTimeZonesModel } from "../../Sessions/indexModel";
import useUrlSeach from "app/utils/hooks/useUrlSearch";
import { useHistory, useLocation } from "react-router-dom";
import { DateTime } from "luxon";
import { toast } from "react-toastify";
import openOnCalendar from "./openOnCalendar";

const NewSession = () => {
  const [searchParams] = useUrlSeach();
  const coach = searchParams.coach;
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const bookLessonState = useSelector((state: RootState) => state.bookSession);
  const { self: user } = useSelector(
    (state: RootState) => state.user,
    shallowEqual
  );

  const fetcher = useCallback(() => {
    if (!coach) {
      return Promise.resolve([]);
    }
    const coachPromise = schedulingFetchGet(
      `/coach_profiles/${coach}`,
      {},
      {},
      null,
      true
    );
    const promise = Promise.all([coachPromise])
      .then(([coach]) => {
        const facilities = [coach["facility"]];
        return [facilities, [coach]];
      })
      .catch((_) => {
        /**
         * The coach could not be opened — the public endpoint is scoped to
         * coaches whose public calendar is on, so this is what an unbookable
         * coach answers.
         *
         * `replace`, not `push`: this entry is the dead end. Pushing left it in
         * the history, so pressing back returned here, the fetch failed again
         * and bounced the visitor away again — a page they could never get out
         * of. Replacing it means back goes where they actually came from.
         */
        history.replace("/");
        toast.error(t("This coach is not taking public bookings."));
      });
    return promise;
  }, [coach]);

  const [loadingBookLessonData, bookLessonData] = useBookSessionData(fetcher);

  useEffect(() => {
    if (user !== null && user.kind !== "athlete" && user.kind !== "other") {
      history.push("/");
      toast.error(t("Only athletes can access this resource."));
    }
  }, [user]);

  useEffect(() => {
    if (loadingBookLessonData || coach === null) {
      return;
    }

    if (!loadingBookLessonData && bookLessonData["facilities"].length === 0) {
      // Same dead end, same reason to replace rather than push.
      history.replace("/");
      toast.error(t("This coach is not taking public bookings."));
      return;
    }

    const initialData = defaultSelection(bookLessonData);
    let lessonState = {
      ...initialData,
      date: initialData.date.toISODate(),
      time: null,
    };
    if (bookLessonState.duration) {
      lessonState = {
        ...lessonState,
        duration: bookLessonState.pricingValue,
      };
    }
    if (bookLessonState.time) {
      /**
       * CFTB-3439: the day comes with the slot.
       *
       * A cell on the facility calendar carries both the coach and the time it was
       * tapped, and this page kept the time while replacing the date with
       * `defaultSelection`'s — today, or the next day anyone is free. So tapping a
       * slot three days out opened the calendar on today, with a selected time
       * belonging to a day not on screen.
       *
       * Derived from the slot rather than read from `bookSession.date`: the slot is
       * what the athlete actually chose, and the stored date is persisted between
       * visits, so a stale one could have opened the calendar in the past.
       */
      const slotZone = bookLessonState.time.timeZone;
      const slotDay = DateTime.fromMillis(
        bookLessonState.time.startHour,
        slotZone ? { zone: slotZone } : {},
      );

      lessonState = {
        ...lessonState,
        time: bookLessonState.time,
        ...(slotDay.isValid ? { date: slotDay.toISODate() } : {}),
      };
    }
    dispatch(setBookSession(lessonState));
    // Following eslint disable is intentional.
    // I do not want this useEffect to rerun if other things change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingBookLessonData, dispatch]);

  const { pending: loadingTimeZones } = useTimeZonesModel();

  if (loadingBookLessonData || !bookLessonState.facility) {
    return <Loading isLoading loadType="spinner" />;
  }

  /**
   * CFTB-3430: a coach's public link opened straight into the calendar with one of
   * their options already chosen for them, so an athlete never saw what else the
   * coach offers. The link now lands on the coach's page — their Private Sessions,
   * Packages and Clinics — and the calendar arrives once a session is picked.
   *
   * The chosen session rides in the URL rather than in state alone, so the browser's
   * back button returns to the options (this page has no back arrow of its own), and
   * a link to a specific session still opens on the calendar. `SessionForm` already
   * reads `sessionId`; booking also dispatches the rate, as the in-app page does.
   */
  const chosenSessionId = searchParams.sessionId;

  /**
   * CFTB-3448: a visitor opening this link sees the coach's sessions, whatever the
   * link says. Only a session settled in this browser — picked on the page below, or
   * carried by a slot — opens the calendar. See `openOnCalendar`.
   */
  const showCalendar = openOnCalendar({
    sessionId: chosenSessionId,
    sessionPicked: (location.state as { sessionPicked?: boolean } | undefined)
      ?.sessionPicked,
    chosenTime: bookLessonState.time,
    selectedSessionId: bookLessonState.selectedSession?.id,
  });

  if (!showCalendar) {
    return (
      <PublicWrapper>
        <CoachDetail
          coachExternalId={bookLessonState.coach?.externalId}
          // Straight from this page's own coach request, so the list does not hang
          // on the landing-page endpoint being open — see `lessonRates` on Props.
          lessonRates={bookLessonState.coach?.lessonRates}
          showFacilityRow={false}
          isPublicPage
          bookRoute={(rate) =>
            `/public/availability?coach=${coach}&sessionId=${rate.id}`
          }
        />
      </PublicWrapper>
    );
  }

  /**
   * Only the calendar waits for the timezone list — it labels slots with the zone.
   * The sessions above need none of it, and gating them on it is what made this page
   * loop: the flag lives in one slice, and the page that gates on it renders a
   * component that asks for the same list.
   */
  if (loadingTimeZones) {
    return <Loading isLoading loadType="spinner" />;
  }

  return (
    <PublicWrapper>
      <SessionForm bookLessonData={bookLessonData} isPublicPage={true} />
    </PublicWrapper>
  );
};
export default NewSession;
